import { useEffect, useRef } from 'react';

import type { ConversationEntry, CourtRuleAuthoritySource, RuntimeStreamProgress } from '../types/runtime';
import { LexAnswerCard } from './LexAnswerCard';

interface Props {
  messages: ConversationEntry[];
  isLoading: boolean;
  streamProgress?: RuntimeStreamProgress[];
  streamingDraft?: string;
  onEvidenceSelect?: (evidenceId: string) => void;
  onCourtRuleSourceOpen?: (source: CourtRuleAuthoritySource) => void;
}

export function LexConversationStream({
  messages,
  isLoading,
  streamProgress = [],
  streamingDraft = '',
  onEvidenceSelect,
  onCourtRuleSourceOpen,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const latestProgress = streamProgress[streamProgress.length - 1];
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isLoading]);

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-6" data-lex-conversation-stream="true">
      {messages.length === 0 && !isLoading && (
        <div className="m-auto max-w-md text-center text-sm text-slate-500">
          <p className="mb-3">Start a conversation with Lex.</p>
          <p className="mb-1">Try one of:</p>
          <ul className="space-y-1 font-mono text-xs text-slate-600">
            <li>Hi</li>
            <li>show related emails</li>
            <li>what does the email say</li>
            <li>what do you suggest next</li>
          </ul>
        </div>
      )}
      {messages.map((entry) =>
        entry.role === 'user' ? (
          <div key={entry.id} className="flex justify-end">
            <div
              className="max-w-[80%] rounded-2xl bg-slate-200 px-4 py-2 text-sm text-slate-900"
              data-lex-selectable="true"
              data-lex-selection-source="chat_user_message"
              data-lex-selection-label="User message"
              data-lex-message-id={entry.id}
            >
              {entry.content}
            </div>
          </div>
        ) : (
          <div key={entry.id} className="flex">
            <div className="w-full max-w-[90%]">
              {entry.turn ? (
                <div
                  data-lex-selectable="true"
                  data-lex-selection-source="chat_answer"
                  data-lex-selection-label="Lex answer"
                  data-lex-message-id={entry.id}
                  data-lex-answer-kind={entry.turn.answer.kind}
                  data-lex-document-id={entry.turn.answer.document_id ?? undefined}
                  data-lex-matter-id={entry.turn.answer.matter_id ?? entry.turn.focus_matter_id ?? undefined}
                >
                  <LexAnswerCard
                    answer={entry.turn.answer}
                    pendingApproval={entry.turn.pending_approval}
                    pendingInteraction={entry.turn.pending_interaction}
                    onEvidenceSelect={onEvidenceSelect}
                    onCourtRuleSourceOpen={onCourtRuleSourceOpen}
                  />
                </div>
              ) : (
                <article
                  className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700"
                  data-lex-selectable="true"
                  data-lex-selection-source="chat_answer"
                  data-lex-selection-label="Lex message"
                  data-lex-message-id={entry.id}
                >
                  {entry.content}
                </article>
              )}
            </div>
          </div>
        ),
      )}
      {isLoading && (
        <div className="flex">
          <div className="max-w-[90%] rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
            {streamingDraft.trim() && (
              <div
                className="mb-3 whitespace-pre-wrap text-slate-800"
                data-lex-selectable="true"
                data-lex-selection-source="chat_streaming_answer"
                data-lex-selection-label="Streaming Lex answer"
              >
                {streamingDraft}
              </div>
            )}
            <div className="font-medium text-slate-700">
              {latestProgress?.label ?? 'Starting LangGraph run'}
            </div>
            {streamProgress.length > 1 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {streamProgress.slice(-4, -1).map((item, index) => (
                  <span
                    key={`${item.label}-${index}`}
                    className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500"
                  >
                    {item.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
